import produce from 'immer';

import {structuredTableActionOrigin} from 'analytics/Util';
import {assert} from 'Asserts';
import {DataModificationsHeader} from 'audit/DataModifications';
import {ActionMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ACTION_SUBMIT_PROCESS_TASKS_ROWS} from 'notebook/store/VersioningConstants';
import makeSubmitRowActionFunction, {
  getRowAwareLoadingMessage,
} from 'notebook/structured-tables/action-functions/makeSubmitRowActionFunction';
import {doPerformCreateTasks} from 'notebook/structured-tables/action-functions/submitProcessTasksActionFunction';
import * as ActionFunctionDAO from 'notebook/structured-tables/ActionFunctionDAO';
import invokeStructuredTableMappings from 'notebook/structured-tables/structured-table-mappings/invokeStructuredTableMappings';
import {
  AxisContextMenuActionFunctionParameters,
  StructuredTableRow,
} from 'notebook/structured-tables/StructuredTableConstants';
import {TableSubmissionDiffs} from 'notebook/structured-tables/util/datamods/getStructuredTableCellDiffOnSubmission';
import {
  ActionResultType,
  performServerSideTableWriteActionWithAudits,
} from 'notebook/util/PerformActionFunctions';
import {CreateOrUpdateServerProcessTask} from 'processes/dao/ProcessManagementDAO';
import getDataModsCountForTableSubmission from 'processes/tables/getDataModsCountForTableSubmission';
import ProcessTaskStructuredTableAdapter, {
  getLastSyncedUpstreamTaskStatus,
  getTaskSchema,
  ProcessTaskStructuredTableMetadata,
  ProcessTaskStructuredTableRowMetadata,
  ProcessTaskStructuredTable,
} from 'processes/tables/ProcessTaskStructuredTableAdapter';
import submitTasksWithConfirmation from 'processes/tables/submitTasksWithConfirmation';
import taskTableToTasks from 'processes/tables/taskTableToTasks';
import APIUtil from 'util/APIUtil';

import {getSourceTypeForStructuredTable} from '../util/StructuredTableAnalyticsUtil';
import {checkStructuredTableValidationErrors} from './validateStructuredTableActionFunction';

export const SUBMIT_PROCESS_TASKS_ROWS_ACTION_FUNCTION_CONFIG = {
  allowOutsideOfEntry: false,
  onSelectForSubmit: async (
    actionFunctionParams: AxisContextMenuActionFunctionParameters<
      ProcessTaskStructuredTableMetadata,
      ProcessTaskStructuredTableRowMetadata
    >
  ) => {
    const {entryOrTemplateId: entryId} = actionFunctionParams;
    assert(entryId != null && APIUtil.isBaseEntry(entryId), 'This action is only supported in entries.', {
      apiId: entryId,
    });
    const taskTableAdapter = actionFunctionParams.structuredTableAdapter as ProcessTaskStructuredTableAdapter;
    const processTaskStructuredTable: ProcessTaskStructuredTable = actionFunctionParams.structuredTable;
    assert(actionFunctionParams.selectionRange != null, 'selectionRange not provided');
    const selectionWithExclusiveEnd = {
      start: actionFunctionParams.selectionRange.minIndex,
      end: actionFunctionParams.selectionRange.maxIndex + 1,
    };
    const slicedProcessTaskRows: Array<StructuredTableRow<ProcessTaskStructuredTableRowMetadata>> =
      processTaskStructuredTable.rows.slice(selectionWithExclusiveEnd.start, selectionWithExclusiveEnd.end);
    const pertinentStructuredTable = produce(processTaskStructuredTable, (table) => {
      table.rows = table.rows.slice(selectionWithExclusiveEnd.start, selectionWithExclusiveEnd.end);
    });
    const pertinentComputedTable = selectionWithExclusiveEnd
      ? actionFunctionParams.computedTable.slice(
          selectionWithExclusiveEnd.start,
          selectionWithExclusiveEnd.end
        )
      : actionFunctionParams.computedTable;
    const tasks = taskTableToTasks({
      table: processTaskStructuredTable,
      evaluatedTable: pertinentComputedTable,
      creationOriginId: entryId,
      sourceType: `entry_${getSourceTypeForStructuredTable(processTaskStructuredTable)}_direct`,
      actionOrigin: structuredTableActionOrigin(processTaskStructuredTable),
      actionMethod: ActionMethod.BUTTON_CLICK,
      structuredTableRowsBatch: slicedProcessTaskRows,
    }) as Array<CreateOrUpdateServerProcessTask>;

    await submitTasksWithConfirmation({
      taskSchemaId: getTaskSchema(processTaskStructuredTable.schemas)!.id,
      tasks: tasks.map((task, idx) => ({
        ...task,
        oldStatusId:
          getLastSyncedUpstreamTaskStatus((slicedProcessTaskRows ?? processTaskStructuredTable.rows)[idx])
            ?.id ?? '',
      })),
      onRequestSubmit: async () => {
        await performServerSideTableWriteActionWithAudits<ActionFunctionDAO.ProcessTaskSubmitResponse>({
          entryId,
          loadingScreenMessage: getRowAwareLoadingMessage(
            selectionWithExclusiveEnd,
            'Submitting Tasks For Rows',
            'Submitting Task For Row'
          ),
          autoSaverAction: ACTION_SUBMIT_PROCESS_TASKS_ROWS,
          doValidationAction: async () => {
            await checkStructuredTableValidationErrors(
              entryId,
              processTaskStructuredTable,
              actionFunctionParams.computedTable,
              actionFunctionParams.actionFunctionContext,
              selectionWithExclusiveEnd
            );
          },
          doPerformAction: (
            tableSubmissionDiffs?: TableSubmissionDiffs,
            updateStatus?: (msg: string) => void,
            dataModsHeader?: DataModificationsHeader
          ) =>
            doPerformCreateTasks(
              entryId,
              processTaskStructuredTable,
              actionFunctionParams.computedTable,
              actionFunctionParams.actionFunctionContext,
              tableSubmissionDiffs,
              selectionWithExclusiveEnd,
              updateStatus,
              dataModsHeader
            ),
          // eslint-disable-next-line require-await -- this does not actually perform any async functionality
          doPostRefresh: async (actionResult) => {
            // Only in Requests do we allow triggering Table Mapping from manually submitting the Task table.
            if (taskTableAdapter.isRequestTaskStructuredTable(processTaskStructuredTable)) {
              invokeStructuredTableMappings(
                entryId,
                processTaskStructuredTable,
                // on a partial success we want to map only the rows that were successfully submitted
                actionResult.resultType === ActionResultType.PARTIAL_SUCCESS
                  ? actionResult.submittedRange
                  : selectionWithExclusiveEnd
              );
            }
          },
          dataModsItemFn: (
            structuredTables,
            structuredTableAdapters,
            computedTables,
            tableSubmissionUpdates
          ) => getDataModsCountForTableSubmission(structuredTables, tableSubmissionUpdates),
          analyticsActionForPerfTracking: 'submit_process_task_structured_table_rows',
          structuredTables: [pertinentStructuredTable],
          structuredTableAdapters: [actionFunctionParams.structuredTableAdapter],
          computedTables: [pertinentComputedTable],
          tables: actionFunctionParams.table ? [actionFunctionParams.table] : [],
          selection: selectionWithExclusiveEnd,
        });
      },
    });
  },
};

export default makeSubmitRowActionFunction<
  ProcessTaskStructuredTableMetadata,
  ProcessTaskStructuredTableRowMetadata
>(SUBMIT_PROCESS_TASKS_ROWS_ACTION_FUNCTION_CONFIG);
